/**
 * ConnectionStatusBadge — shows the connection/identity verification status of a person profile.
 *
 * Statuses:
 *   private_placeholder  — Private archive profile, not yet verified or claimed
 *   verification_pending — A verification request has been noted; awaiting response
 *   verified             — Verified connection (the person has confirmed)
 *   declined             — Declined or removed
 *   claimed              — This person has joined MyLegacyBridge and claimed their profile
 *   shared_profile       — (internal default; no badge shown)
 */
import React from 'react';
import { Shield, Clock, CheckCircle2, XCircle, BadgeCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_MAP = {
  private_placeholder: {
    label: 'Private placeholder',
    shortLabel: 'Private',
    icon: Shield,
    colorClass: 'border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/70 text-muted-foreground',
    iconClass: 'text-[hsl(var(--studio-accent,38_36%_42%))]',
    description:
      'Private placeholders help you organise your archive. A verified connection means the other person has confirmed or claimed their connection.',
    hint: (name) =>
      `${name || 'This person'} has not yet joined or claimed this profile. You can add stories and memories here, and invite them when the time is right.`,
  },
  verification_pending: {
    label: 'Verification pending',
    shortLabel: 'Pending',
    icon: Clock,
    colorClass: 'border-amber-200/60 bg-amber-50/80 text-amber-700',
    iconClass: 'text-amber-500',
    description: 'A verification request has been noted. This person will be able to confirm their connection when they join MyLegacyBridge.',
    hint: (name) =>
      `${name || 'This person'} has been marked as pending verification. They can confirm their connection when they join MyLegacyBridge.`,
  },
  verified: {
    label: 'Verified connection',
    shortLabel: 'Verified',
    icon: CheckCircle2,
    colorClass: 'border-green-200/60 bg-green-50/80 text-green-700',
    iconClass: 'text-green-500',
    description: 'This person has confirmed their connection to this archive.',
    hint: (name) => `${name || 'This person'} has verified their connection to this archive.`,
  },
  declined: {
    label: 'Declined',
    shortLabel: 'Declined',
    icon: XCircle,
    colorClass: 'border-[hsl(38_14%_86%)]/60 bg-muted/40 text-muted-foreground/70',
    iconClass: 'text-muted-foreground/50',
    description: 'This connection request was declined.',
    hint: (name) => `${name || 'This person'} declined the connection request.`,
  },
  claimed: {
    label: 'Claimed profile',
    shortLabel: 'Claimed',
    icon: BadgeCheck,
    colorClass: 'border-emerald-200/60 bg-emerald-50/80 text-emerald-700',
    iconClass: 'text-emerald-500',
    description: 'This person has joined MyLegacyBridge and claimed their archive profile.',
    hint: (name) => `${name || 'This person'} has joined and claimed their archive profile.`,
  },
};

/**
 * Full badge — used in person overview card.
 * @param {{ person: object, className?: string }} props
 */
export function ConnectionStatusBadge({ person, className = '' }) {
  const status = person?.identity_status;
  if (!status || status === 'shared_profile') return null;

  const config = STATUS_MAP[status];
  if (!config) return null;

  const Icon = config.icon;
  const name = person?.given_names || person?.full_name;

  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2.5 text-left',
        config.colorClass,
        className
      )}
      data-testid={`connection-status-badge-${status}`}
    >
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.12em]">
        <Icon className={cn('h-3 w-3 shrink-0', config.iconClass)} aria-hidden />
        {config.label}
      </p>
      <p className="mt-1 text-xs leading-relaxed opacity-90">{config.hint(name)}</p>
      {status === 'private_placeholder' && (
        <p className="mt-1.5 text-[10px] leading-relaxed opacity-70 italic">
          {config.description}
        </p>
      )}
    </div>
  );
}

/**
 * Compact inline pill — used in family connection rows, lists, cards.
 * @param {{ status: string, className?: string }} props
 */
export function ConnectionStatusPill({ status, className = '' }) {
  if (!status || status === 'shared_profile') return null;
  const config = STATUS_MAP[status];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
        config.colorClass,
        className
      )}
      title={config.label}
      data-testid={`connection-status-pill-${status}`}
    >
      <Icon className={cn('h-2.5 w-2.5 shrink-0', config.iconClass)} aria-hidden />
      {config.shortLabel}
    </span>
  );
}

export function getConnectionStatusConfig(status) {
  return STATUS_MAP[status] || null;
}

export default ConnectionStatusBadge;
